import { useCallback, useEffect, useMemo, useState } from 'react';
import { Project } from '../../../models/Project';
import { SiteProjectService } from '../../../services/SiteProjectService';
import { SortKey, SortDir } from '../types';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

interface UseSiteManagementResult {
  sites: Project[]; // 전체(필터·정렬 전)
  filteredSortedSites: Project[]; // 필터 + 정렬 적용
  pagedSites: Project[]; // 최종 페이지 슬라이스
  totalItems: number; // filtered 기준
  page: number; // 1-based
  pageSize: number;
  searchTerm: string;
  sortBy: SortKey;
  sortDir: SortDir;
  loading: boolean;
  error: string | null;
  onSearchChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onSortChange: (key: SortKey) => void;
  refresh: () => Promise<void>;
}

/**
 * 현장 목록 상태 훅.
 * - 목록 로드: SiteProjectService.listProjects
 * - 검색: 이름/위치 contains (대소문자 무시), 300ms debounce
 * - 정렬: 이름(name) 또는 최근 배포 시각(lastDeployedAt)
 *   * lastDeployedAt 정렬은 외부(상위 View)에서 주입한 `getLastDeployedAt(site)` 맵으로 해결해야 하나,
 *     module-4 스코프에서는 `name` 정렬만 안전하게 구현하고 lastDeployedAt 은 id 기반 폴백을 사용.
 *     실제 lastDeployedAt 정렬은 module-5 View 조립 단계에서 조합.
 * - 페이지네이션: 클라이언트 사이드, 10개/페이지
 *
 * 반환 `sites`는 전체 목록. Row 조립(배포 정보 병합)은 호출자가 담당.
 */
export function useSiteManagement(): UseSiteManagementResult {
  const [sites, setSites] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [sortBy, setSortBy] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const siteProjects = await SiteProjectService.listProjects();
      const projects: Project[] = siteProjects.map((sp) => ({
        id: sp.id.toString(),
        name: sp.name,
        name_ko: sp.name_ko,
        router_in_use_count: sp.router_in_use_count,
        description: sp.description,
        location: sp.location,
        address: sp.address,
        status: sp.status,
        // BUG-FIX(2026-05-12): 기존 매핑이 dashboard_url 을 누락 → openEdit 으로 전달되는
        // site 에 항상 undefined → CreateSiteDialog 가 빈 필드로 열렸음. 또한 row 클릭으로
        // localStorage 에 저장될 때도 누락되어 ProjectDashboardPage 의 Grafana 버튼이
        // 비활성화되는 원인이기도 했음.
        dashboard_url: sp.dashboard_url,
        construction_completed_at: sp.construction_completed_at,
        final_completed_at: sp.final_completed_at,
      }));
      setSites(projects);
    } catch (e: unknown) {
      console.error('현장 목록 로드 실패:', e);
      setError('현장 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // search debounce
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim().toLowerCase());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchTerm]);

  const filteredSortedSites = useMemo(() => {
    const filtered = debouncedSearch
      ? sites.filter(
          (s) =>
            s.name.toLowerCase().includes(debouncedSearch) ||
            (s.name_ko ?? '').toLowerCase().includes(debouncedSearch) ||
            (s.location ?? '').toLowerCase().includes(debouncedSearch),
        )
      : sites;

    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'name') {
        const cmp = a.name.localeCompare(b.name, 'ko');
        return sortDir === 'asc' ? cmp : -cmp;
      }
      // lastDeployedAt: module-4 스코프 폴백 — id desc. module-5 에서 Row 조립 시 재정렬 가능.
      const cmp = a.id.localeCompare(b.id);
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [sites, debouncedSearch, sortBy, sortDir]);

  const pagedSites = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredSortedSites.slice(start, start + PAGE_SIZE);
  }, [filteredSortedSites, page]);

  const onSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const onPageChange = useCallback((next: number) => {
    setPage(Math.max(1, next));
  }, []);

  const onSortChange = useCallback((key: SortKey) => {
    setSortBy((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return key;
    });
    setPage(1);
  }, []);

  return {
    sites,
    filteredSortedSites,
    pagedSites,
    totalItems: filteredSortedSites.length,
    page,
    pageSize: PAGE_SIZE,
    searchTerm,
    sortBy,
    sortDir,
    loading,
    error,
    onSearchChange,
    onPageChange,
    onSortChange,
    refresh,
  };
}
